import * as Sentry from '@sentry/react'

import axios, { AxiosError } from 'axios'
import { OnOfficeSessionEndpoint, PropstackSessionEndpoint, getApiUrl } from 'constants/API'
import { Nullable } from 'models/helpers'

import { logger } from 'utils/logging'

/**
 * Dedicated axios instance for the session EXCHANGE/REFRESH calls. Using the global `axios`
 * would route them through the embed 401 interceptor: a 401 from the refresh endpoint would
 * re-enter the interceptor, which awaits `refresh()` — the single-flight then hands back the
 * SAME in-flight promise, a circular wait that never settles (every embed request would hang).
 */
const sessionHttp = axios.create()

/**
 * The server events' `workspace_type` wire values (backend TrackedWorkspaceType). A broker session only ever yields
 * `shared_iv24` (OneSystem broker) or `shop_owned` (any other Propstack broker); `direct` exists for a workspace
 * with no CRM behind it and never reaches the embed.
 */
export type EmbedWorkspaceType = 'shop_owned' | 'shared_iv24' | 'direct'

const EMBED_WORKSPACE_TYPES: readonly EmbedWorkspaceType[] = ['shop_owned', 'shared_iv24', 'direct']

/**
 * The backend's value when the embed knows it, else null: a value it does not know (a renamed or new backend value,
 * casing drift, blank) must not reach Mixpanel as is, where it would silently stop matching the server events.
 */
const toEmbedWorkspaceType = (value: unknown): EmbedWorkspaceType | null =>
  typeof value === 'string' && EMBED_WORKSPACE_TYPES.includes(value as EmbedWorkspaceType)
    ? (value as EmbedWorkspaceType)
    : null

export interface EmbedSessionContext {
  /** NORMATIVE workspace pin (ISSUE-2889): the embed must operate in this workspace, never the user's own. */
  workspaceId: string
  isOneSystem: boolean
  /** The launch's shop (ISSUE-3213), an analytics dimension. Optional only for rollout tolerance. */
  shopId?: string
  /**
   * The launch this session belongs to (ISSUE-3961): the server launch events carry it as `launch_attempt_id`.
   * Null for a bootstrap token minted before the backend sent it; optional for rollout tolerance, like shopId.
   */
  launchAttemptId?: Nullable<string>
  /**
   * The kind of workspace the session operates in (ISSUE-3994): describes the workspace, where `isOneSystem`
   * describes the broker. Optional only for rollout tolerance, like shopId. Typed as what the wire carries; the
   * validated value (`EmbedWorkspaceType`) lives in the state and the getter.
   */
  workspaceType?: Nullable<string>
  /**
   * The broker's Auth0 id (ISSUE-4339), the distinct_id of the server events, which the embed identifies with in
   * Mixpanel. Optional only for rollout tolerance, like shopId.
   */
  auth0UserId?: Nullable<string>
}

export interface EmbedSessionPair {
  accessToken: string
  accessExpiresIn: number
  refreshToken: string
  refreshExpiresIn: number
  tokenType: string
  /** Optional only for rollout tolerance (an older backend omits it); current backends always send it. */
  context?: EmbedSessionContext
}

/** The CRM whose iframe opened the session: it decides which platform endpoints exchange and refresh it. */
export type EmbedSessionProvider = 'propstack' | 'onoffice'

const SESSION_ENDPOINTS: Record<EmbedSessionProvider, { exchange: string, refresh: string }> = {
  propstack: { exchange: PropstackSessionEndpoint.EXCHANGE, refresh: PropstackSessionEndpoint.REFRESH },
  onoffice: { exchange: OnOfficeSessionEndpoint.EXCHANGE, refresh: OnOfficeSessionEndpoint.REFRESH },
}

/** Refresh proactively once this fraction of the access-token lifetime has elapsed. */
const PROACTIVE_REFRESH_RATIO = 0.8

interface EmbedSessionState {
  provider: EmbedSessionProvider
  accessToken: string
  refreshToken: string
  accessExpiresAt: number
  accessIssuedAt: number
  workspaceId: string | null
  isOneSystem: boolean | null
  shopId: string | null
  launchAttemptId: string | null
  workspaceType: EmbedWorkspaceType | null
  auth0UserId: string | null
}

/**
 * In-memory broker session for the Propstack embed (ISSUE-2338). The pair NEVER touches
 * localStorage or the URL: memory survives tab sleep (the overnight/weekend case) and a frame
 * reload intentionally drops the session — Propstack's entry point re-inits with a fresh
 * bootstrap token.
 */
let state: EmbedSessionState | null = null
let refreshInFlight: Promise<void> | null = null
// The CRM of the last session this page load opened. Outlives clear() and a failed refresh, so the screens explaining
// a lost session still name the CRM the broker came from.
let lastProvider: EmbedSessionProvider | null = null
// When the exchange landed (never moved by a refresh) and whether the first rendered screen already
// reported its latency against it (ISSUE-3218): once per exchange, so an in-SPA re-entry reports nothing.
let sessionStartedAt: number | null = null
let firstScreenLatencyClaimed = false
// Once per session: a refresh that keeps reporting another launch id would otherwise warn every few minutes.
let launchAttemptIdChangeReported = false

/**
 * Activation listeners: the auth layer (useAuth0) mirrors `isActive()` reactively via
 * useSyncExternalStore, so root-level providers (UserDataContext, appMeta) re-render when the
 * exchange lands — they mount before EmbedGuard resolves and would otherwise never re-read it.
 */
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

/**
 * Stamps every Sentry event raised inside the session with the workspace it is scoped to, and with its launch
 * (ISSUE-3961) so an embed error can be joined to the server launch events.
 */
const publishSessionTags = () => {
  Sentry.setTag('embedProvider', state?.provider ?? 'none')
  Sentry.setTag('embedWorkspaceId', state?.workspaceId ?? 'none')
  // A live session without a launch id (unknown) stays distinguishable from no session (none).
  Sentry.setTag('embedLaunchAttemptId', state ? state.launchAttemptId ?? 'unknown' : 'none')
}

/**
 * A refresh must carry the exchange's launch id (the refresh tokens copy the claim), so a different one, or one where
 * the exchange had none, is a backend bug (a refresh minting a new launch, crossed sessions). The backend stays
 * authoritative for the session, so the refreshed context is adopted like any other, but Mixpanel keeps the exchange's
 * id until the next reload: the change is reported once per session, or the symptom would be an unexplained break in
 * the launch -> order funnel join.
 */
const reportLaunchAttemptIdChange = (incomingLaunchAttemptId: string | null) => {
  if (
    launchAttemptIdChangeReported ||
    state === null ||
    !incomingLaunchAttemptId ||
    incomingLaunchAttemptId === state.launchAttemptId
  ) {
    return
  }
  launchAttemptIdChangeReported = true
  logger.warn('Embed session refresh reported a different launchAttemptId', undefined, {
    context: {
      component: 'embedSession',
      action: 'launchAttemptIdChangedOnRefresh',
      metadata: {
        stored: state.launchAttemptId,
        refreshed: incomingLaunchAttemptId,
        workspaceId: state.workspaceId,
        shopId: state.shopId,
      },
    },
  })
}

const storePair = (pair: EmbedSessionPair, provider: EmbedSessionProvider) => {
  const now = Date.now()
  const incomingLaunchAttemptId = pair.context?.launchAttemptId?.trim() || null
  reportLaunchAttemptIdChange(incomingLaunchAttemptId)
  lastProvider = provider
  state = {
    provider,
    accessToken: pair.accessToken,
    refreshToken: pair.refreshToken,
    accessExpiresAt: now + pair.accessExpiresIn * 1000,
    accessIssuedAt: now,
    // A context-less refresh (older backend during rollout) must not drop the exchange's pin;
    // a blank id is treated as absent so it cannot clobber it either.
    workspaceId: pair.context?.workspaceId?.trim() || state?.workspaceId || null,
    isOneSystem: pair.context?.isOneSystem ?? state?.isOneSystem ?? null,
    shopId: pair.context?.shopId?.trim() || state?.shopId || null,
    launchAttemptId: incomingLaunchAttemptId || state?.launchAttemptId || null,
    workspaceType: toEmbedWorkspaceType(pair.context?.workspaceType) || state?.workspaceType || null,
    auth0UserId: pair.context?.auth0UserId?.trim() || state?.auth0UserId || null,
  }
  publishSessionTags()
  notify()
}

const bootstrap = async (bootstrapToken: string, provider: EmbedSessionProvider = 'propstack'): Promise<void> => {
  const { data } = await sessionHttp.post<EmbedSessionPair>(
    `${getApiUrl()}${SESSION_ENDPOINTS[provider].exchange}`,
    undefined,
    { headers: { Authorization: `Bearer ${bootstrapToken}` } }
  )
  if (provider === 'propstack') warnOnIncompletePropstackContext(data)
  // storePair's carry-over exists for a context-less REFRESH: a fresh exchange must never inherit
  // the previous session's pin, so drop the state first.
  state = null
  sessionStartedAt = Date.now()
  firstScreenLatencyClaimed = false
  launchAttemptIdChangeReported = false
  storePair(data, provider)
}

/**
 * The Propstack session context carries the analytics dimensions (shop, launch, workspace type); an onOffice one
 * only carries the workspace, so these checks are Propstack's alone.
 */
const warnOnIncompletePropstackContext = (data: EmbedSessionPair) => {
  if (!data.context) {
    logger.warn('Embed session exchange returned no context; workspace pin unavailable', undefined, {
      context: { component: 'embedSession', action: 'exchangeWithoutContext' },
    })
  } else if (!data.context.shopId) {
    // The shop dimension (ISSUE-3213) is what the analytics funnels break down by; a backend that stops
    // sending it would otherwise look like a healthy funnel with one dimension missing.
    logger.warn('Embed session exchange context carries no shopId', undefined, {
      context: { component: 'embedSession', action: 'exchangeWithoutShopId' },
    })
  }
  if (data.context && !data.context.launchAttemptId?.trim()) {
    // Same reasoning as shopId: without it the browser funnel silently stops joining the server launch events.
    // The reason tells the cases apart: null = a token minted before the backend change (rollout, short-lived);
    // missing = the backend omits the field (a frontend released ahead of BE #584, or a regression once both are
    // live); blank = a serialization bug.
    const { launchAttemptId, workspaceId, shopId } = data.context
    const reason = launchAttemptId === undefined ? 'missing' : launchAttemptId === null ? 'null' : 'blank'
    logger.warn('Embed session exchange context carries no launchAttemptId', undefined, {
      context: {
        component: 'embedSession',
        action: 'exchangeWithoutLaunchAttemptId',
        metadata: { reason, workspaceId, shopId },
      },
    })
  }
  if (data.context && !toEmbedWorkspaceType(data.context.workspaceType)) {
    // Same reasoning as shopId: the server events carry workspace_type, so a browser funnel without it cannot be
    // broken down the same way (ISSUE-3994). The reason tells a backend predating the field (missing) apart from a
    // value the embed does not know (unknown), which is dropped rather than registered.
    const { workspaceType, workspaceId, shopId } = data.context
    const reason = workspaceType === undefined ? 'missing' : 'unknown'
    logger.warn('Embed session exchange context carries no usable workspaceType', undefined, {
      context: {
        component: 'embedSession',
        action: 'exchangeWithoutWorkspaceType',
        metadata: { reason, workspaceType, workspaceId, shopId },
      },
    })
  }
  if (data.context && !data.context.auth0UserId?.trim()) {
    // Same reasoning as shopId: the embed then identifies only once /user/me loads, so its first events can land on
    // whoever this browser identified last (ISSUE-4339). The reason tells a backend without the field apart from a bug.
    const { auth0UserId, workspaceId, shopId } = data.context
    const reason = auth0UserId === undefined ? 'missing' : auth0UserId === null ? 'null' : 'blank'
    logger.warn('Embed session exchange context carries no auth0UserId', undefined, {
      context: {
        component: 'embedSession',
        action: 'exchangeWithoutAuth0UserId',
        metadata: { reason, workspaceId, shopId },
      },
    })
  }
}

const doRefresh = async (refreshToken: string, provider: EmbedSessionProvider, proactive: boolean): Promise<void> => {
  try {
    const { data } = await sessionHttp.post<EmbedSessionPair>(
      `${getApiUrl()}${SESSION_ENDPOINTS[provider].refresh}`,
      undefined,
      { headers: { Authorization: `Bearer ${refreshToken}` } }
    )
    storePair(data, provider)
  } catch (error) {
    // A PROACTIVE refresh (fired at ~80% of the access lifetime) can fail on a transient blip
    // while the current access token is still valid — keep the session and let the reactive 401
    // path recover a real expiry later. Only drop the session when the refresh fails AND the
    // access token is already expired, or on the explicit reactive refresh path (the token was
    // rejected / the single-use refresh token was burned server-side, so recovery is impossible).
    const status = (error as AxiosError)?.response?.status
    const accessExpired = state === null || Date.now() >= state.accessExpiresAt
    if (!proactive || accessExpired) {
      state = null
      publishSessionTags()
      notify()
      logger.warn('Embed session refresh failed; session cleared', { status }, {
        context: { component: 'embedSession', action: 'refreshFailed', metadata: { provider } },
      })
    }
    throw error
  }
}

const refresh = async (proactive = false): Promise<void> => {
  if (!state) throw new Error('No embed session to refresh')
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(state.refreshToken, state.provider, proactive).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

const shouldProactivelyRefresh = (): boolean => {
  if (!state) return false
  const lifetime = state.accessExpiresAt - state.accessIssuedAt
  return Date.now() >= state.accessIssuedAt + lifetime * PROACTIVE_REFRESH_RATIO
}

const getAccessToken = async (): Promise<string | null> => {
  if (!state) return null
  if (shouldProactivelyRefresh()) {
    try {
      await refresh(true)
    } catch (error) {
      // Proactive refresh failed. doRefresh keeps the session when the current access token is
      // still valid (and clears it once expired), so fall through and serve whatever remains —
      // the still-valid token, or null once the session was dropped. Log a REDACTED scalar so
      // the failure is visible (a raw 401 AxiosError would be dropped by the logger's 401 filter
      // and would leak the bearer via error.config.headers).
      const status = (error as AxiosError)?.response?.status
      logger.warn('Embed session proactive refresh failed', { status }, {
        context: { component: 'embedSession', action: 'proactiveRefreshFailed' },
      })
    }
  }
  return state?.accessToken ?? null
}

export const embedSession = {
  bootstrap,
  refresh,
  getAccessToken,
  /** The session's workspace pin — null outside an active embed session. */
  getWorkspaceId: () => state?.workspaceId ?? null,
  /** The session's OneSystem channel — null when unknown (no session, or a context-less exchange). */
  getIsOneSystem: () => state?.isOneSystem ?? null,
  /** The session's shop (ISSUE-3213) — null when unknown. */
  getShopId: () => state?.shopId ?? null,
  /** The session's launch (ISSUE-3961), the join key with the server launch events — null when unknown. */
  getLaunchAttemptId: () => state?.launchAttemptId ?? null,
  /** The kind of workspace the session operates in (ISSUE-3994), the server events' workspace_type — null when unknown. */
  getWorkspaceType: () => state?.workspaceType ?? null,
  /** The broker's Auth0 id (ISSUE-4339), the server events' distinct_id — null when unknown. */
  getAuth0UserId: () => state?.auth0UserId ?? null,
  /**
   * Milliseconds from the exchange to now, handed out once per exchange for the first rendered screen's
   * load latency (ISSUE-3218); null afterwards, without a session, and for an in-SPA re-entry.
   */
  claimFirstScreenLatency: (): number | null => {
    if (!state || sessionStartedAt === null || firstScreenLatencyClaimed) return null
    firstScreenLatencyClaimed = true
    return Date.now() - sessionStartedAt
  },
  /** The CRM whose iframe opened the session — null outside an active embed session. */
  getProvider: () => state?.provider ?? null,
  /** The CRM of the last session opened in this page load, kept after the session is cleared — null if none was. */
  getLastProvider: () => lastProvider,
  isActive: () => state !== null,
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  clear: () => {
    state = null
    sessionStartedAt = null
    firstScreenLatencyClaimed = false
    launchAttemptIdChangeReported = false
    publishSessionTags()
    notify()
  },
}
